import { useEffect, useState } from 'react';
import api from '../../services/api';
import { toast } from 'sonner';
import { Mail, Plus, Trash2, Send, Pause, Play, Clock, Calendar, RefreshCw } from 'lucide-react';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';

/**
 * Admin Report Emails page.
 *
 * Admins subscribe to recurring daily/weekly digests. Each subscription
 * owns its own cadence + delivery email, and can be enabled/disabled or
 * sent immediately (preview) without touching the schedule.
 */
export default function ReportEmailsPage() {
  const [subs, setSubs] = useState([]);
  const [loading, setLoading] = useState(false);
  const [email, setEmail] = useState('');
  const [schedule, setSchedule] = useState('daily');
  const [hourUtc, setHourUtc] = useState(9);
  const [creating, setCreating] = useState(false);

  const load = async () => {
    setLoading(true);
    try {
      const res = await api.get('/api/admin/report-emails');
      setSubs(res.data?.subscriptions || []);
    } catch {
      toast.error('Failed to load report email subscriptions');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load(); }, []);

  const create = async () => {
    if (!email.trim()) {
      toast.error('Email is required');
      return;
    }
    setCreating(true);
    try {
      await api.post('/api/admin/report-emails', {
        email: email.trim(),
        schedule,
        hour_utc: hourUtc,
      });
      toast.success('Subscription created');
      setEmail('');
      load();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Failed to create');
    } finally {
      setCreating(false);
    }
  };

  const toggle = async (sub) => {
    try {
      await api.patch(`/api/admin/report-emails/${sub.id}`, {
        enabled: !sub.enabled,
      });
      load();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Failed to update');
    }
  };

  const sendNow = async (sub) => {
    try {
      const res = await api.post(`/api/admin/report-emails/${sub.id}/send-now`);
      if (res.data?.ok) {
        toast.success('Preview dispatched — check the inbox');
      } else {
        toast.warning(res.data?.detail || 'Dispatch returned non-OK');
      }
      load();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Send failed');
    }
  };

  const remove = async (sub) => {
    if (!window.confirm(`Delete subscription for ${sub.email}?`)) return;
    try {
      await api.delete(`/api/admin/report-emails/${sub.id}`);
      toast.success('Deleted');
      load();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Delete failed');
    }
  };

  return (
    <div className="p-6 space-y-6" data-testid="report-emails-page">
      <div>
        <h1 className="text-2xl font-bold text-white">Report Emails</h1>
        <p className="text-sm text-gray-400 mt-1">
          Automated daily / weekly digests with revenue, signups, referral channels, and top depositors.
        </p>
      </div>

      {/* Create form */}
      <div className="bg-[#131822] border border-white/5 rounded-2xl p-5 space-y-4" data-testid="report-emails-create-card">
        <div className="flex items-center gap-2">
          <Plus className="w-4 h-4 text-[#D4AF37]" />
          <h2 className="text-sm font-semibold text-white">New Subscription</h2>
        </div>
        <div className="grid grid-cols-12 gap-3">
          <div className="col-span-12 md:col-span-5">
            <label className="block text-[11px] uppercase text-gray-500 mb-1">Delivery email</label>
            <Input
              type="email"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              placeholder="owner@example.com"
              className="bg-[#0B0E14] border-white/10"
              data-testid="report-emails-email-input"
            />
          </div>
          <div className="col-span-6 md:col-span-3">
            <label className="block text-[11px] uppercase text-gray-500 mb-1">Cadence</label>
            <select
              value={schedule}
              onChange={(e) => setSchedule(e.target.value)}
              className="w-full bg-[#0B0E14] border border-white/10 rounded-md px-3 py-2 text-sm text-white h-10"
              data-testid="report-emails-schedule-select"
            >
              <option value="daily">Daily</option>
              <option value="weekly">Weekly (Mon)</option>
            </select>
          </div>
          <div className="col-span-6 md:col-span-2">
            <label className="block text-[11px] uppercase text-gray-500 mb-1">Hour (UTC)</label>
            <Input
              type="number"
              min={0}
              max={23}
              value={hourUtc}
              onChange={(e) => setHourUtc(Number(e.target.value))}
              className="bg-[#0B0E14] border-white/10"
              data-testid="report-emails-hour-input"
            />
          </div>
          <div className="col-span-12 md:col-span-2 flex items-end">
            <Button
              onClick={create}
              disabled={creating || !email.trim()}
              className="w-full bg-[#D4AF37] hover:bg-[#c39d2f] text-black font-bold"
              data-testid="report-emails-create-btn"
            >
              {creating ? 'Creating…' : 'Subscribe'}
            </Button>
          </div>
        </div>
      </div>

      {/* List */}
      <div className="bg-[#131822] border border-white/5 rounded-2xl overflow-hidden">
        <div className="px-4 py-3 border-b border-white/5 flex items-center justify-between">
          <div className="flex items-center gap-2">
            <Mail className="w-4 h-4 text-[#D4AF37]" />
            <h2 className="text-sm font-semibold text-white">Active Subscriptions</h2>
            <span className="text-[11px] text-gray-500">{subs.length}</span>
          </div>
          <Button variant="ghost" size="sm" onClick={load} disabled={loading} className="gap-1" data-testid="report-emails-refresh">
            <RefreshCw className={`w-3.5 h-3.5 ${loading ? 'animate-spin' : ''}`} /> Refresh
          </Button>
        </div>
        {subs.length === 0 ? (
          <div className="px-4 py-10 text-center text-sm text-gray-500">
            No subscriptions yet. Add one above to start receiving digests.
          </div>
        ) : (
          <div className="divide-y divide-white/5">
            {subs.map((s) => (
              <SubscriptionRow
                key={s.id}
                sub={s}
                onToggle={() => toggle(s)}
                onSendNow={() => sendNow(s)}
                onRemove={() => remove(s)}
              />
            ))}
          </div>
        )}
      </div>
    </div>
  );
}

function SubscriptionRow({ sub, onToggle, onSendNow, onRemove }) {
  const nextRun = sub.next_run_at ? new Date(sub.next_run_at).toLocaleString() : '—';
  const lastRun = sub.last_run_at ? new Date(sub.last_run_at).toLocaleString() : 'never';
  return (
    <div className="px-4 py-3 grid grid-cols-12 gap-3 items-center text-sm" data-testid={`report-emails-row-${sub.id}`}>
      <div className="col-span-4 min-w-0">
        <div className="text-white font-medium truncate">{sub.email}</div>
        <div className="text-[11px] text-gray-500">
          {sub.schedule === 'weekly' ? 'Weekly' : 'Daily'} · {String(sub.hour_utc).padStart(2, '0')}:00 UTC
        </div>
      </div>
      <div className="col-span-3 text-xs text-gray-400 flex items-center gap-1">
        <Calendar className="w-3.5 h-3.5" /> Next: {nextRun}
      </div>
      <div className="col-span-2 text-xs text-gray-400 flex items-center gap-1">
        <Clock className="w-3.5 h-3.5" /> Last: {lastRun}
        {sub.last_status && (
          <span className={`ml-1 text-[10px] ${sub.last_status === 'sent' ? 'text-emerald-400' : 'text-red-400'}`}>
            · {sub.last_status.startsWith('failed') ? 'failed' : sub.last_status}
          </span>
        )}
      </div>
      <div className="col-span-3 flex justify-end gap-1.5">
        <Button
          size="sm"
          variant="outline"
          onClick={onSendNow}
          className="border-white/10 h-7 text-[11px] gap-1"
          data-testid={`report-emails-send-now-${sub.id}`}
        >
          <Send className="w-3 h-3" /> Send now
        </Button>
        <Button
          size="sm"
          variant="outline"
          onClick={onToggle}
          className={`h-7 text-[11px] gap-1 ${sub.enabled ? 'border-white/10' : 'border-[#D4AF37]/30 text-[#D4AF37]'}`}
          data-testid={`report-emails-toggle-${sub.id}`}
        >
          {sub.enabled ? <><Pause className="w-3 h-3" /> Pause</> : <><Play className="w-3 h-3" /> Resume</>}
        </Button>
        <button
          onClick={onRemove}
          className="p-1.5 rounded-lg hover:bg-red-500/10 text-red-400"
          title="Delete"
          data-testid={`report-emails-delete-${sub.id}`}
        >
          <Trash2 className="w-3.5 h-3.5" />
        </button>
      </div>
    </div>
  );
}
